import { Alert, Stack } from "@mantine/core";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { useToast } from "../../../../mantine/notifications/useToast";
import urls, { OptiSupportEndpoints } from "../../../../urls";
import type { TodoCategory, TodoFile } from "../../../../utils/backend-types";
import { AnchorLink } from "../../../AnchorLink/AnchorLink";
import { DeleteConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import { MarkdownLink } from "../../../ReactMarkdown/MarkdownLink";
import { useTodoCanGenerateDocuments } from "../../hooks/useTodoCanGenerateDocuments";
import { useTodos } from "../../hooks/useTodos";
import { OnboardingTodo } from "../../Todos.constants";
import type { Mode } from "../../Todos.types";
import { isTodoSpecificOnboardingTodo } from "../../utils/isTodoSpecificOnboardingTodo";
import { DocumentsDropzone } from "./DocumentsDropzone/DocumentsDropzone";
import { DocumentsList } from "./DocumentsList/DocumentsList";
import { GenerateDocuments } from "./GenerateDocuments/GenerateDocuments";
import "./DocumentsWidget.scss";

interface DocumentsWidgetProps {
  documents: Array<TodoFile>;
  mode: Mode;
  todoId: number;
  todoLabel: string;
  todoCategory: TodoCategory;
  variantId: number;
  showTodoStatus?: boolean;
  isRevokeable?: boolean;
  handleErrorMessage: (message: string) => void;
}

function DocumentsWidget({
  mode,
  todoId,
  todoLabel,
  variantId,
  showTodoStatus,
  isRevokeable,
  handleErrorMessage
}: DocumentsWidgetProps) {
  const { showErrorToast } = useToast();
  const {
    todos,
    storeTodo,
    addTodoDocuments,
    renameTodoDocument,
    removeTodoDocument,
    removeTodoDocuments,
    updateDocumentProvided,
    reloadTodo,
    notify
  } = useTodos({ mode, variantId });

  const { todoCanGenerateDocuments } = useTodoCanGenerateDocuments(
    todoId,
    handleErrorMessage
  );

  const documents = todos?.find((todo) => todo.id === todoId)?.todoFiles || [];
  const documentProvided = todos?.find(
    (todo) => todo.id === todoId
  )?.documentProvided;

  const [documentToDelete, setDocumentToDelete] = useState<TodoFile | null>(
    null
  );

  function handleClickRenameSingleDocument(
    document: TodoFile,
    newName: string
  ) {
    if (document.name !== newName) {
      renameTodoDocument({ todoId, documentId: document.id, newName }).catch(
        (error) => {
          showErrorToast(error);
        }
      );
    }
  }

  function handleClickDeleteSingleDocument(document: TodoFile) {
    setDocumentToDelete(document);
  }

  function handleConfirmDeleteSingleDocument(
    documentToDelete: TodoFile | null
  ) {
    if (!documentToDelete) {
      return;
    }

    removeTodoDocument({
      todoId: todoId,
      documentId: documentToDelete.id
    })
      .then(() => {
        setDocumentToDelete(null);
      })
      .catch((error) => {
        showErrorToast(error);
      });
  }

  function deleteMultipleDocuments(documentIds: Array<number>) {
    return removeTodoDocuments({ todoId, documentIds });
  }

  const showEnergyDataLink = isTodoSpecificOnboardingTodo(
    todoLabel,
    OnboardingTodo.HISTORISCHE_MESSDATEN_AUS_UNTERMESSSYSTEMEN_BEREITSTELLEN
  );
  const showDocumentsList = !showEnergyDataLink || documents.length > 0;

  return (
    <Stack className="DocumentsWidget">
      {todoCanGenerateDocuments &&
        todoCanGenerateDocuments?.warnings?.map((text, index) => (
          <Warning key={index} text={text} />
        ))}

      {showDocumentsList && (
        <DocumentsList
          deleteDocuments={deleteMultipleDocuments}
          documentProvided={documentProvided}
          documents={documents}
          handleErrorMessage={handleErrorMessage}
          isRevokeable={isRevokeable}
          mode={mode}
          showTodoStatus={showTodoStatus}
          storeTodo={storeTodo}
          todoId={todoId}
          updateDocumentProvided={updateDocumentProvided}
          variantId={variantId}
          onClickDelete={handleClickDeleteSingleDocument}
          onClickNotify={notify}
          onClickRename={handleClickRenameSingleDocument}
        />
      )}
      <GenerateDocuments
        handleErrorMessage={handleErrorMessage}
        mode={mode}
        reloadTodo={reloadTodo}
        storeTodo={storeTodo}
        todoId={todoId}
        todoLabel={todoLabel}
        variantId={variantId}
      />
      {showEnergyDataLink ? (
        <EnergyDataLinkWidget padded={showDocumentsList} />
      ) : (
        <DocumentsDropzone
          addTodoDocuments={addTodoDocuments}
          todoId={todoId}
        />
      )}
      <DeleteConfirmationModal
        name={documentToDelete?.name ?? "Unbekanntes Dokument"}
        opened={!!documentToDelete}
        toggleModal={() => setDocumentToDelete(null)}
        onConfirm={() => handleConfirmDeleteSingleDocument(documentToDelete)}
      />
    </Stack>
  );
}

interface EnergyDataLinkWidgetProps {
  padded: boolean;
}

function EnergyDataLinkWidget({ padded }: EnergyDataLinkWidgetProps) {
  return (
    <Stack align="center" p={padded ? "50px 0" : undefined}>
      <p style={{ textAlign: "center" }}>
        Wir haben den Prozess zum Bereitstellen der Energiedaten für Sie
        automatisiert und sind daher umgezogen. Über den Button gelangen Sie
        direkt zum neuen Energiedatenimport. Als Hilfestellung finden Sie
        ebenfalls eine ausführliche Anleitung{" "}
        <AnchorLink
          href={urls.optiSupport(
            OptiSupportEndpoints.EnergiedatenBereitstellen
          )}
        >
          hier
        </AnchorLink>
        .
      </p>
    </Stack>
  );
}

function Warning({ text }: { text: string }) {
  return (
    <Alert
      className="document-generation-warning"
      color={AlertPresets.warning.color}
    >
      <ReactMarkdown
        components={{
          a: MarkdownLink
        }}
      >
        {text}
      </ReactMarkdown>
    </Alert>
  );
}

export { DocumentsWidget, DocumentsWidgetProps };
